import { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  Users,
  Search,
  Eye,
  UserCheck,
  UserX,
  Calendar,
  Mail,
  Phone,
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  Download
} from 'lucide-react';

interface User {
  id: number;
  name: string;
  email: string;
  mobile: string;
  targetYear: number;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLogin: string | null;
}

interface UserDetails extends User {
  parentEmail?: string;
  preferredSubjects: string[];
  updatedAt: string;
  subscription?: {
    plan: string;
    status: string;
    endDate: string;
  };
  stats?: {
    testsCompleted: number;
    averageScore: number;
    totalTimeSpent: number;
    lastTestDate: string | null;
  };
  usageLimit?: {
    testsUsed: number;
    testsLimit: number;
    practiceTestsUsed: number;
    practiceTestsLimit: number;
  };
}

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, sets] = useState({
    role: 'student',
    status: 'all'
  });

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        role: filters.role,
        ...(filters.status !== 'all' && { isActive: filters.status === 'active' })
      };

      const response = await adminAPI.getUsers(params);
      if (response.data.success) {
        // Handle response structure - data is the array directly, pagination is separate
        const usersData = response.data.data || [];
        const paginationData = response.data.pagination || { pages: 1 };
        
        setUsers(usersData);
        setTotalPages(paginationData.pages);
      }
    } catch (error: any) {
      toast.error('Failed to load users');
      console.error('Fetch users error:', error);
      // Set empty array on error to prevent undefined errors
      setUsers([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId: number) => {
    try {
      setDetailsLoading(true);
      const response = await adminAPI.getUserDetails(userId.toString());
      if (response.data.success) {
        // Handle response structure - data is the user object directly
        setSelectedUser(response.data.data);
        setShowUserDetails(true);
      }
    } catch (error: any) {
      toast.error('Failed to load user details');
      console.error('Fetch user details error:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const toggleUserStatus = async (userId: number, currentStatus: boolean) => {
    try {
      await adminAPI.updateUserStatus(userId.toString(), !currentStatus);
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (error: any) {
      toast.error('Failed to update user status');
      console.error('Update user status error:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'danger';
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-2">User Management</h1>
            <p className="text-sm md:text-base text-secondary-600">Manage and monitor platform users</p>
          </div>
          <button className="flex items-center justify-center px-4 py-2.5 md:py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors">
            <Download className="h-4 w-4 mr-2" />
            Export Users
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card mb-6">
        <div className="card-content">
          <div className="flex flex-col space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 md:py-2 w-full border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={filters.role}
                onChange={(e) => sets({ ...filters, role: e.target.value })}
                className="flex-1 min-w-[120px] px-3 py-2.5 md:py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="student">Students</option>
                <option value="admin">Admins</option>
              </select>
              <select
                value={filters.status}
                onChange={(e) => sets({ ...filters, status: e.target.value })}
                className="flex-1 min-w-[120px] px-3 py-2.5 md:py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="flex items-center space-x-2 text-sm text-secondary-600 ml-auto">
                <Users className="h-4 w-4" />
                <span>{(users || []).length} users found</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-content p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-secondary-600">Loading users...</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-full">
                  <thead className="bg-secondary-50 border-b border-secondary-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Target Year</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Last Login</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-secondary-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                              <span className="text-primary-700 font-medium text-sm">{user.name.charAt(0).toUpperCase()}</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-secondary-900">{user.name}</div>
                              <div className="text-sm text-secondary-500">ID: {user.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-secondary-900 flex items-center"><Mail className="h-4 w-4 mr-2 text-secondary-400" />{user.email}</div>
                          <div className="text-sm text-secondary-500 flex items-center mt-1"><Phone className="h-4 w-4 mr-2 text-secondary-400" />{user.mobile}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-secondary-900"><GraduationCap className="h-4 w-4 mr-2 text-secondary-400" />{user.targetYear}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`badge badge-${getStatusColor(user.isActive)}`}>{user.isActive ? 'Active' : 'Inactive'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                          <div className="flex items-center"><Calendar className="h-4 w-4 mr-2 text-secondary-400" />{formatDate(user.createdAt)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button onClick={() => fetchUserDetails(user.id)} className="text-primary-600 hover:text-primary-900 p-1" title="View Details"><Eye className="h-4 w-4" /></button>
                            <button onClick={() => toggleUserStatus(user.id, user.isActive)} className={`p-1 ${user.isActive ? 'text-danger-600 hover:text-danger-900' : 'text-success-600 hover:text-success-900'}`} title={user.isActive ? 'Deactivate' : 'Activate'}>
                              {user.isActive ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card list */}
              <div className="md:hidden divide-y divide-secondary-200">
                {users.map((user) => (
                  <div key={user.id} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-700 font-medium text-sm">{user.name.charAt(0).toUpperCase()}</span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-secondary-900">{user.name}</div>
                          <div className="text-xs text-secondary-500">{user.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button onClick={() => fetchUserDetails(user.id)} className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg" title="View Details"><Eye className="h-4 w-4" /></button>
                        <button onClick={() => toggleUserStatus(user.id, user.isActive)} className={`p-2 rounded-lg ${user.isActive ? 'text-danger-600 hover:bg-danger-50' : 'text-success-600 hover:bg-success-50'}`} title={user.isActive ? 'Deactivate' : 'Activate'}>
                          {user.isActive ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-secondary-500 mt-1">
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{user.mobile}</span>
                      <span className="flex items-center gap-1"><GraduationCap className="h-3 w-3" />{user.targetYear}</span>
                      <span className={`badge badge-${getStatusColor(user.isActive)} text-[10px]`}>{user.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
          <div className="text-sm text-secondary-600">Page {currentPage} of {totalPages}</div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="flex items-center px-3 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-secondary-900">User Details</h3>
                <button
                  onClick={() => setShowUserDetails(false)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {detailsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
                  <p className="text-secondary-600">Loading user details...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* User Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">Name</label>
                      <p className="text-secondary-900">{selectedUser.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">Email</label>
                      <p className="text-secondary-900">{selectedUser.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">Mobile</label>
                      <p className="text-secondary-900">{selectedUser.mobile}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">Target Year</label>
                      <p className="text-secondary-900">{selectedUser.targetYear}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">Status</label>
                      <span className={`badge badge-${getStatusColor(selectedUser.isActive)}`}>
                        {selectedUser.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">Joined</label>
                      <p className="text-secondary-900">{formatDate(selectedUser.createdAt)}</p>
                    </div>
                  </div>

                  {/* Preferred Subjects */}
                  {selectedUser.preferredSubjects && selectedUser.preferredSubjects.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Preferred Subjects</label>
                      <div className="flex flex-wrap gap-2">
                        {selectedUser.preferredSubjects.map((subject) => (
                          <span key={subject} className="badge badge-primary">
                            {subject.charAt(0).toUpperCase() + subject.slice(1)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Subscription Info */}
                  {selectedUser.subscription && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Subscription</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-3 bg-secondary-50 rounded-lg">
                          <p className="text-xs text-secondary-600 mb-1">Plan</p>
                          <p className="text-sm font-medium text-secondary-900 capitalize">{selectedUser.subscription.plan}</p>
                        </div>
                        <div className="p-3 bg-secondary-50 rounded-lg">
                          <p className="text-xs text-secondary-600 mb-1">Status</p>
                          <span className={`badge badge-${selectedUser.subscription.status === 'active' ? 'success' : 'warning'}`}>
                            {selectedUser.subscription.status}
                          </span>
                        </div>
                        <div className="p-3 bg-secondary-50 rounded-lg">
                          <p className="text-xs text-secondary-600 mb-1">End Date</p>
                          <p className="text-sm font-medium text-secondary-900">{formatDate(selectedUser.subscription.endDate)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Usage Limits */}
                  {selectedUser.usageLimit && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Usage Limits</label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-3 bg-secondary-50 rounded-lg">
                          <p className="text-xs text-secondary-600 mb-1">Mock Tests</p>
                          <p className="text-sm font-medium text-secondary-900">
                            {selectedUser.usageLimit.testsUsed} / {selectedUser.usageLimit.testsLimit}
                          </p>
                          <div className="w-full bg-secondary-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-primary-500 h-2 rounded-full" 
                              style={{ width: `${(selectedUser.usageLimit.testsUsed / selectedUser.usageLimit.testsLimit) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="p-3 bg-secondary-50 rounded-lg">
                          <p className="text-xs text-secondary-600 mb-1">Practice Tests</p>
                          <p className="text-sm font-medium text-secondary-900">
                            {selectedUser.usageLimit.practiceTestsUsed} / {selectedUser.usageLimit.practiceTestsLimit}
                          </p>
                          <div className="w-full bg-secondary-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-success-500 h-2 rounded-full" 
                              style={{ width: `${(selectedUser.usageLimit.practiceTestsUsed / selectedUser.usageLimit.practiceTestsLimit) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Statistics */}
                  {selectedUser.stats && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Performance Statistics</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-3 bg-secondary-50 rounded-lg">
                          <p className="text-xs text-secondary-600 mb-1">Tests Completed</p>
                          <p className="text-2xl font-bold text-secondary-900">{selectedUser.stats.testsCompleted || 0}</p>
                        </div>
                        <div className="p-3 bg-secondary-50 rounded-lg">
                          <p className="text-xs text-secondary-600 mb-1">Average Score</p>
                          <p className="text-2xl font-bold text-primary-600">
                            {selectedUser.stats.averageScore ? selectedUser.stats.averageScore.toFixed(1) : '0.0'}%
                          </p>
                        </div>
                        <div className="p-3 bg-secondary-50 rounded-lg">
                          <p className="text-xs text-secondary-600 mb-1">Total Time</p>
                          <p className="text-2xl font-bold text-secondary-900">
                            {selectedUser.stats.totalTimeSpent ? Math.round(selectedUser.stats.totalTimeSpent / 60) : 0}h
                          </p>
                        </div>
                      </div>
                      {selectedUser.stats.lastTestDate && (
                        <p className="text-xs text-secondary-600 mt-2">
                          Last test: {formatDate(selectedUser.stats.lastTestDate)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
